import React, { useState, useEffect } from 'react';
import { CalendarCheck, Flame, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Card } from '../../components/UI';
import { checkinsApi } from '../../api';
import './Checkin.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MOODS = [
  { value: 'great', label: '😊 Great', color: '#2C4A3E' },
  { value: 'good', label: '🙂 Good', color: '#C9A962' },
  { value: 'okay', label: '😐 Okay', color: '#5A6978' },
  { value: 'tired', label: '😴 Tired', color: '#8B3A44' },
  { value: 'stressed', label: '😓 Stressed', color: '#722F37' },
];

const Checkin = () => {
  const [checkins, setCheckins] = useState([]);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [streak, setStreak] = useState({ streak: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [note, setNote] = useState('');
  const [selectedMood, setSelectedMood] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchMonthCheckins();
  }, [currentMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [todayRes, streakRes] = await Promise.all([
        checkinsApi.getToday(),
        checkinsApi.getStreak()
      ]);
      setTodayCheckin(todayRes.data.checkin);
      setStreak(streakRes.data);
      if (todayRes.data.checkin) {
        setNote(todayRes.data.checkin.note || '');
        setSelectedMood(todayRes.data.checkin.mood || '');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthCheckins = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await checkinsApi.getAll(year, month.toString());
      setCheckins(response.data);
    } catch (error) {
      console.error('Error fetching checkins:', error);
    }
  };

  const handleCheckin = async () => {
    try {
      await checkinsApi.create({
        note: note,
        mood: selectedMood
      });
      fetchData();
      fetchMonthCheckins();
    } catch (error) {
      console.error('Error checking in:', error);
    }
  };

  const changeMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isCheckedIn = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return checkins.some(c => c.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === currentMonth.getMonth() && 
           today.getFullYear() === currentMonth.getFullYear();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const checked = isCheckedIn(day);
      const today = isToday(day);
      
      days.push(
        <div 
          key={day} 
          className={`calendar-day ${checked ? 'checked' : ''} ${today ? 'today' : ''}`}
        >
          <span className="day-number">{day}</span>
          {checked && <Check size={14} className="check-icon" />}
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="checkin-page">
      <div className="page-header">
        <div>
          <h1>Daily Check-in</h1>
          <p className="page-subtitle">Stay consistent, build momentum</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-row">
        <Card className="stat-card">
          <div className="stat-icon streak">
            <Flame size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{streak.streak}</div>
            <div className="stat-label">Current Streak</div>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-icon total">
            <CalendarCheck size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{streak.total}</div>
            <div className="stat-label">Total Check-ins</div>
          </div>
        </Card>
      </div>

      {/* Today's Check-in */}
      <Card className="today-card" variant="gold">
        <h2>Today's Check-in</h2>
        
        {todayCheckin ? (
          <div className="checked-in-message">
            <div className="checked-badge">
              <Check size={20} />
              <span>You've checked in today!</span>
            </div>
            {todayCheckin.mood && (
              <p className="mood-display">
                Mood: {MOODS.find(m => m.value === todayCheckin.mood)?.label}
              </p>
            )}
            {todayCheckin.note && (
              <p className="note-display">"{todayCheckin.note}"</p>
            )}
          </div>
        ) : (
          <div className="checkin-form">
            <div className="mood-selector">
              <label>How are you feeling today?</label>
              <div className="mood-options">
                {MOODS.map(mood => (
                  <button
                    key={mood.value}
                    className={`mood-btn ${selectedMood === mood.value ? 'selected' : ''}`}
                    onClick={() => setSelectedMood(mood.value)}
                    style={{ '--mood-color': mood.color }}
                  >
                    {mood.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="note-input">
              <label>Quick note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What's on your mind today?"
                rows={3}
              />
            </div>

            <Button variant="gold" size="large" fullWidth onClick={handleCheckin}>
              <Check size={18} />
              Check In Now
            </Button>
          </div>
        )}
      </Card>

      {/* Calendar */}
      <Card className="calendar-card">
        <div className="calendar-header">
          <h3>Check-in History</h3>
          <div className="calendar-nav">
            <button className="nav-btn" onClick={() => changeMonth(-1)}>
              <ChevronLeft size={20} />
            </button>
            <span className="current-month">
              {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button className="nav-btn" onClick={() => changeMonth(1)}>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-grid">
          {renderCalendar()}
        </div>

        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-dot checked" />
            <span>Checked in</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot today" />
            <span>Today</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Checkin;
